import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import SubscriptionTierCard from './components/SubscriptionTierCard';
import PaymentForm from './components/PaymentForm';
import TrustSignals from './components/TrustSignals';



const UserRegistration = () => {
  const navigate = useNavigate();
  const { signUp, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [signupError, setSignupError] = useState('');
  const [signupStatus, setSignupStatus] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [pendingEmail, setPendingEmail] = useState('');

  const subscriptionTiers = [
    {
      id: 'free',
      name: 'Free',
      description: 'Try before you commit',
      price: 0,
      quota: 'Limited features',
      features: [
        'Access to basic AI agents',
        'Create 1 book outline',
        'Community forum access',
        'Email support'
      ]
    },
    {
      id: 'starter',
      name: 'Starter',
      description: 'Perfect for first-time authors',
      price: 49,
      quota: '1 book per month',
      features: [
        'All Free features',
        'Complete 1 book per month',
        'Full AI agent access',
        'Skool community access',
        'Priority email support',
        'KDP formatting tools'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      description: 'For serious content creators',
      price: 99,
      quota: '4 books per month',
      features: [
        'All Starter features',
        'Complete 4 books per month',
        'Advanced AI customization',
        'Cover design templates',
        'Marketing guidance',
        'Live chat support'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      description: 'Unlimited publishing power',
      price: 999,
      quota: 'Unlimited books',
      features: [
        'All Professional features',
        'Unlimited book creation',
        'White-label options',
        'Dedicated account manager',
        'Custom AI training',
        'API access',
        '24/7 priority support'
      ]
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData?.fullName || formData?.fullName?.trim()?.length < 2) {
      newErrors.fullName = 'Please enter your full name';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData?.email || !emailRegex?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password || formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/?.test(formData?.password)) {
      newErrors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!agreedToTerms) {
      newErrors.terms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleStep1Submit = async (e) => {
    e?.preventDefault();
    setSignupError('');
    setSignupStatus('');

    if (!validateStep1()) return;

    setIsSubmitting(true);
    try {
      const result = await signUp(
        formData?.email,
        formData?.password,
        {
          full_name: formData?.fullName,
          role: 'author'
        }
      );

      if (!result?.user && !result?.session) {
        throw new Error('We could not create your account. Please try again.');
      }

      // Store the email so we can reuse it on resend/redirect
      setPendingEmail(formData?.email?.trim());

      const requiresConfirmation = result?.user && !result?.session;
      if (requiresConfirmation) {
        setSignupStatus(`Account created! We sent a confirmation link to ${formData?.email?.trim()}. Confirm your email, then sign in.`);
      } else {
        setSignupStatus('Account created! You are signed in. Choose a plan to continue.');
      }

      setCurrentStep(2);
    } catch (error) {
      setSignupError(error?.message || 'Failed to create account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTierSelection = (tier) => {
    setSelectedTier(tier);
    if (tier?.id === 'free') {
      handleFreeRegistration();
    } else {
      setCurrentStep(3);
    }
  };

  const handleFreeRegistration = () => {
    // If the user already has a session, go straight to the dashboard
    if (user) {
      navigate('/user-dashboard', { replace: true });
      return;
    }

    // Otherwise, prompt them to confirm email and log in
    navigate('/user-login', {
      replace: true,
      state: {
        email: pendingEmail || formData?.email,
        message: 'Account created. Please confirm your email, then sign in.'
      }
    });
  };

  const handlePaymentComplete = () => {
    // Payment simulated; rely on Supabase session for auth
    if (user) {
      navigate('/user-dashboard', { replace: true });
      return;
    }

    navigate('/user-login', {
      replace: true,
      state: {
        email: pendingEmail || formData?.email,
        message: 'Payment complete. Please confirm your email (if required) and sign in.'
      }
    });
  };

  const getPasswordStrength = () => {
    const password = formData?.password;
    if (!password) return { strength: 0, label: '', color: '' };

    let strength = 0;
    if (password?.length >= 8) strength++;
    if (/[a-z]/?.test(password)) strength++;
    if (/[A-Z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[^a-zA-Z\d]/?.test(password)) strength++;

    const labels = ['', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    const colors = ['', 'bg-error', 'bg-warning', 'bg-warning', 'bg-success', 'bg-success'];

    return {
      strength: (strength / 5) * 100,
      label: labels?.[strength],
      color: colors?.[strength]
    };
  };

  const passwordStrength = getPasswordStrength();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3">
                Start Your Author Journey Today
              </h1>
              <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                Join thousands of entrepreneurs building authority and credibility through AI-powered book creation
              </p>
            </div>

            <div className="flex items-center justify-center mb-8">
              <div className="flex items-center space-x-2">
                {[1, 2, 3]?.map((step) => (
                  <React.Fragment key={step}>
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
                      currentStep >= step
                        ? 'bg-primary border-primary text-primary-foreground'
                        : 'bg-card border-border text-muted-foreground'
                    }`}>
                      {currentStep > step ? (
                        <Icon name="Check" size={20} />
                      ) : (
                        <span className="text-sm font-semibold">{step}</span>
                      )}
                    </div>
                    {step < 3 && (
                      <div className={`w-16 h-0.5 transition-all duration-300 ${
                        currentStep > step ? 'bg-primary' : 'bg-border'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {currentStep === 1 && (
              <div className="grid lg:grid-cols-2 gap-8">
                <div>
                  <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
                    <h2 className="text-xl font-heading font-semibold text-foreground mb-6">
                      Create Your Account
                    </h2>

                    {signupError && (
                      <div className="mb-4 p-3 rounded-md border border-error bg-error/10 text-sm text-error">
                        {signupError}
                      </div>
                    )}

                    {signupStatus && (
                      <div className="mb-4 p-3 rounded-md border border-success bg-success/10 text-sm text-success">
                        {signupStatus}
                      </div>
                    )}

                    <form onSubmit={handleStep1Submit} className="space-y-4">
                      <Input
                        label="Full Name"
                        type="text"
                        name="fullName"
                        placeholder="John Doe"
                        value={formData?.fullName}
                        onChange={handleInputChange}
                        error={errors?.fullName}
                        required
                      />

                      <Input
                        label="Email Address"
                        type="email"
                        name="email"
                        placeholder="john@example.com"
                        description="We'll never share your email with anyone"
                        value={formData?.email}
                        onChange={handleInputChange}
                        error={errors?.email}
                        required
                      />

                      <div>
                        <Input
                          label="Password"
                          type="password"
                          name="password"
                          placeholder="Create a strong password"
                          value={formData?.password}
                          onChange={handleInputChange}
                          error={errors?.password}
                          required
                        />
                        {formData?.password && (
                          <div className="mt-2">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-muted-foreground">
                                Password strength:
                              </span>
                              <span className={`text-xs font-medium ${
                                passwordStrength?.label === 'Weak' ? 'text-error' :
                                passwordStrength?.label === 'Fair'|| passwordStrength?.label === 'Good' ? 'text-warning' : 'text-success'
                              }`}>
                                {passwordStrength?.label}
                              </span>
                            </div>
                            <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                              <div
                                className={`h-full transition-all duration-300 ${passwordStrength?.color}`}
                                style={{ width: `${passwordStrength?.strength}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                      <Input
                        label="Confirm Password"
                        type="password"
                        name="confirmPassword"
                        placeholder="Re-enter your password"
                        value={formData?.confirmPassword}
                        onChange={handleInputChange}
                        error={errors?.confirmPassword}
                        required
                      />

                      <Checkbox
                        label="I agree to the Terms of Service and Privacy Policy"
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e?.target?.checked)}
                        error={errors?.terms}
                      />

                      <Button
                        type="submit"
                        variant="default"
                        size="lg"
                        fullWidth
                        iconName="ArrowRight"
                        iconPosition="right"
                        loading={isSubmitting}
                      >
                        {isSubmitting ? 'Creating account...' : 'Continue to Plan Selection'}
                      </Button>
                    </form>

                    <div className="mt-6 pt-6 border-t border-border text-center">
                      <p className="text-sm text-muted-foreground">
                        Already have an account?{' '}
                        <button
                          onClick={() => navigate('/user-login')}
                          className="text-primary hover:underline font-medium"
                        >
                          Sign in
                        </button>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
                    <div className="flex items-start space-x-3 mb-4">
                      <Icon name="Sparkles" size={24} className="text-primary flex-shrink-0" />
                      <div>
                        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                          Why Choose AuthorPreneur AI?
                        </h3>
                        <ul className="space-y-2 text-sm text-foreground">
                          <li className="flex items-start space-x-2">
                            <Icon name="Check" size={16} className="flex-shrink-0 mt-0.5 text-success" />
                            <span>Create professional books in weeks, not months</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Icon name="Check" size={16} className="flex-shrink-0 mt-0.5 text-success" />
                            <span>AI agents guide you through every step</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Icon name="Check" size={16} className="flex-shrink-0 mt-0.5 text-success" />
                            <span>Build authority and unlock speaking opportunities</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <Icon name="Check" size={16} className="flex-shrink-0 mt-0.5 text-success" />
                            <span>Join exclusive Skool community of authors</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <TrustSignals />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-8">
                <div className="text-center">
                  <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                    Choose Your Plan
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Select the plan that best fits your publishing goals
                  </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {subscriptionTiers?.map((tier) => (
                    <SubscriptionTierCard
                      key={tier?.id}
                      tier={tier}
                      isSelected={selectedTier?.id === tier?.id}
                      onSelect={() => handleTierSelection(tier)}
                      isPopular={tier?.id === 'professional'}
                    />
                  ))}
                </div>

                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentStep(1)}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Back to Account Details
                  </Button>
                </div>

                <TrustSignals />
              </div>
            )}

            {currentStep === 3 && selectedTier && (
              <div className="max-w-2xl mx-auto">
                <PaymentForm
                  selectedTier={selectedTier}
                  onPaymentComplete={handlePaymentComplete}
                  onBack={() => setCurrentStep(2)}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default UserRegistration;
